import { Box } from '@mui/material';
import { ChangeEvent } from 'react';

import { ClearInputTextButton } from '@/components/form/baseInputs/Button/ClearInputTextButton';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { Loader } from '@/components/progress/Loader/Loader';

/**
 * Handles both loading indicator and clear button for the search input
 * Maintains consistent positioning to avoid flashing between states
 */
function SearchInputAdornment({
  loading,
  hasValue,
  onClear,
}: {
  loading: boolean;
  hasValue: boolean;
  onClear: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        position: 'absolute',
        right: 8,
        gap: 1,
      }}
    >
      {loading && <Loader circularProgressProps={{ size: 15 }} />}
      {hasValue && <ClearInputTextButton onClick={onClear} />}
    </Box>
  );
}

interface FilterSearchInputProps {
  /** Current search text value (controlled) */
  value: string;
  /** Called when the input value changes */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether to show a loading spinner */
  loading?: boolean;
}

/**
 * Controlled search input for filtering options within a filter group.
 * State and debouncing should be handled by the parent component.
 */
export function FilterSearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  loading = false,
}: FilterSearchInputProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onChange(event.target.value);
  };

  const handleClear = () => {
    onChange('');
  };

  return (
    <Box sx={{ px: 0.5, py: 0.5 }}>
      <TextInput
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        hideLabel
        label="Search filters"
        startAdornment={<SearchSmIcon size={16} />}
        fullWidth
        onFocus={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        endAdornment={
          <SearchInputAdornment
            loading={loading}
            hasValue={value.length > 0}
            onClear={handleClear}
          />
        }
      />
    </Box>
  );
}
